import React, { useState, useEffect } from "react";
import { Button, Box, styled, Grid, FormLabel } from "@mui/material";
import MxInputNumberField from "../text-fields/MxInputNumberField";
import MxTypography from "@rf-l1/MxTypography";

const SelectButton = styled(Button)<{ selected: boolean }>(
  ({ theme, selected }) => ({
    fontFamily: "var(--ff-inter)",
    display: "flex",
    WebkitBoxPack: "center",
    justifyContent: "center",
    WebkitBoxAlign: "center",
    alignItems: "center",
    height: "25px",
    fontSize: 12,
    cursor: "pointer",
    fontWeight: "bold",
    border: "1px solid var(--color-gray-02) !important",
    borderRadius: "20px",
    minWidth: "3rem",
    maxWidth: "3.5rem",
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.grey[200],
    color: selected ? "#fff" : theme.palette.text.primary,
    "&:hover": {
      backgroundColor: selected
        ? theme.palette.primary.dark
        : theme.palette.grey[300]
    }
  })
);

const ContentButton = styled(Grid)(({ theme }) => ({
  display: "grid",
  // gridTemplateColumns: "repeat(8, 1fr)",
  width: "100%",
  flexDirection: "row",
  columnGap: "0.2rem",
  height: "25px"
}));

const StyledInput = styled(Box)(({ theme }) => ({
  "*": {
    padding: "0 !important"
  },
  "&  .MuiTextField-root": {
    fontFamily: "var(--ff-inter)",
    display: "flex",
    WebkitBoxPack: "center",
    justifyContent: "center",
    WebkitBoxAlign: "center",
    alignItems: "center",
    fontSize: 12,
    cursor: "pointer",
    fontWeight: "bold",
    borderRadius: "20px",
    minWidth: "3.2rem",
    maxWidth: "3.13em",
    textAlign: "center",
    width: "100%",

    "&  input": {
      textAlign: "center",
      height: "25px",
      display: "flex"
    }
  }
}));

export interface MxRushCounterProps {
  countRender: number;
  multiple?: boolean;
  maxMultiple?: number;
  label: string;
  value?: (string | number)[];
  name: string;
  onChange: (e: { target: { name: string; value: (string | number)[] } }) => void;
  onBlur: (e: React.FocusEvent<(string | number)[]> | any) => void;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  id?: string;
}

const MxRushCounter: React.FC<MxRushCounterProps> = (props: MxRushCounterProps) => {
  const {
    countRender: count,
    multiple = false,
    maxMultiple = 2,
    label,
    value = [],
    name,
    onChange,
    onBlur
  } = props;
  const [selectedValues, setSelectedValues] = useState<(string | number)[]>([]);
  const [inputValue, setInputValue] = useState("");
  const refInput = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const safeValue = Array.isArray(value) ? value : [];
    const i = safeValue.findIndex(x => {
      const parsed = parseInt(`${x}`);
      return !isNaN(parsed) && parsed >= count;
    });

    if (i >= 0) {
      setInputValue(`${safeValue[i]}`);
    } else {
      setInputValue(``);
      setSelectedValues(safeValue);
    }
  }, [value, count]);

  const options = [...Array(count).keys()];

  const handleSelect = (selectedValue: string | number) => {
    let newSelectedValues: (string | number)[];

    if (multiple) {
      if (selectedValues.includes(selectedValue)) {
        newSelectedValues = selectedValues.filter(v => v !== selectedValue);
      } else if (selectedValues.length < maxMultiple) {
        newSelectedValues = [...selectedValues, selectedValue];
      } else {
        newSelectedValues = [selectedValue, ...selectedValues];
        newSelectedValues.pop();
      }
    } else {
      if (selectedValues.includes(selectedValue)) {
        newSelectedValues = [];
      } else {
        newSelectedValues = [selectedValue];
      }
    }

    setSelectedValues(newSelectedValues);
    onChange({ target: { name, value: newSelectedValues } });
    onBlur({ target: { name, value: newSelectedValues } });
  };

  const handleInputChange = (e: any) => {
    setInputValue(e.target.value);
  };

  const handleInputSubmit = (e: any) => {
    if (refInput.current?.value === "") {
      return;
    }
    const parsedValue = parseInt(refInput.current?.value || "0");
    if (!isNaN(parsedValue)) {
      handleSelect(parsedValue);
    } else {
    }
  };

  return (
    <Grid container item xs={12}>
      <Grid item xs={12} mb={1}>
        <MxTypography
          variant="caption"
          fontWeight={600}
          color={"primary"}
          component={FormLabel}
          className={""}
          data-testid={`${props.id}-label`}
        >
          {label}
        </MxTypography>
      </Grid>

      <Grid container item xs={12} gap={1}>
        {options.map((option, index) => (
          <SelectButton
            key={index}
            selected={selectedValues.includes(option)}
            onClick={() => handleSelect(option)}
            data-testid={`${props.id}-select-button-${option}`}
          >
            {option}
          </SelectButton>
        ))}
        <StyledInput>
          <MxInputNumberField
            value={inputValue}
            inputRef={refInput}
            onChange={handleInputChange}
            onBlur={handleInputSubmit}
            fullWidth
            placeholder="#"
            id={props.id}
          />
        </StyledInput>
      </Grid>
    </Grid>
  );
};

export default MxRushCounter;
